/**
 * 👤🤖 What KIND of seat is this? (Nina 08-15)
 *
 * ⭐ `is_human` IS NOT "this is a person". A connected external agent — a
 * marketplace agent, a probe, anything driving a seat over the API — is seated
 * with `is_human: true` deliberately: the game engine's only question is
 * "should I call a model for this seat, or wait for input to arrive?", and for
 * a connected agent the answer is the same as for a person. See
 * `gameManagementApi.addAgentPlayer` (~:766) and its docblock, which says so
 * outright.
 *
 * Every DISPLAY surface then read that flag as "a human being is here", which
 * is how SmokeProbe ended up rendering as "🧑 Human" on the cast rail, counting
 * toward "N visitors here now", and getting filtered out of the @mention list
 * (you literally could not @ a visiting agent).
 *
 * The backend has always shipped the real discriminator — `is_external_agent`,
 * plus the narrower `is_visiting_agent` and the seat's own `agent_model`. This
 * module is the one place that reads them, so the next surface cannot get it
 * wrong again.
 *
 * ⚠️ DISPLAY AND COUNTING ONLY. Do NOT reach for `isRealHuman` to gate
 * behaviour: the backend intentionally lets external agents use slash commands
 * and take turns through the human path (`handler.js`: "Only humans can use
 * commands" is an `is_human` check, and agents pass it on purpose). Swapping a
 * behaviour gate to this predicate would mute every visiting agent.
 *
 * Seats arrive in two shapes depending on the surface — flat from the status
 * payload, or nested under `.data` from raw player rows — so every reader here
 * checks both.
 */

/** True only for an actual person. Use for labels, counts and roster copy. */
export const isRealHuman = (u) =>
  !!(u && (u.is_human || u.data?.is_human) && !isExternalAgent(u));

/** True for any API-driven agent seat, founding cast or guest. */
export const isExternalAgent = (u) =>
  !!(u && (u.is_external_agent || u.data?.is_external_agent));

/**
 * 🔧 True for the admin's own seat (09-01). Stamped server-side at join only
 * when the browser presented a valid admin key (handler.js checkAdminAuth) —
 * never settable from the join body. Orthogonal to every other flag: the seat
 * is still a real human. Drives the "system maintenance" tag next to the
 * anonymous name.
 */
export const isMaintainer = (u) =>
  !!(u && (u.is_maintainer || u.data?.is_maintainer));

/**
 * True for a GUEST agent — one that dropped in via the marketplace and holds a
 * timed seat. Deliberately narrower than `isExternalAgent`: a founding ported
 * agent (created with the space, no `agent_id`) is cast, not a visitor, and
 * must never be captioned as one. Falls back to `is_external_agent` for
 * payloads that predate `is_visiting_agent`.
 */
export const isVisitingAgent = (u) =>
  !!(u && (u.is_visiting_agent || u.data?.is_visiting_agent
    || (isExternalAgent(u) && u.is_visiting_agent === undefined && u.data?.is_visiting_agent === undefined)));

/**
 * 🪪 The model the backend used to stamp on EVERY agent seat that reported none
 * (fixed 09-26: a connected seat now gets none). A connected seat still carrying
 * it was never told this by its agent, so it shows no model (Nina 09-28: no heal
 * pass for old seats, so the display drops it). Imported seats keep it: there it
 * IS the model we run.
 */
const INVENTED_DEFAULT_MODEL = 'google/gemini-3-flash-preview';

const isConnectedSeat = (u) => (u?.agent_type || u?.data?.agent_type) === 'connected';

/** The model actually driving an agent seat, or '' when it is not an agent. */
export const agentModelName = (u) => {
  if (!u || !isExternalAgent(u)) return '';
  const raw = u.agent_model || u.data?.agent_model
    // 'AI' is the status-poll mapper's placeholder (GameStepPage), never a model.
    || (u.model && u.model !== 'Human Player' && u.model !== 'AI' ? u.model : '') || u.data?.model || '';
  if (!raw) return '';
  if (isConnectedSeat(u) && String(raw) === INVENTED_DEFAULT_MODEL) return '';
  return String(raw).split('/')[1] || String(raw);
};

/**
 * The caption under an agent's name (Nina 08-15: "the name that an agent picks
 * when it joins a space, then the model name, then a visiting agent identifier
 * of some sort"). The NAME is the seat's own `name` — an agent chooses it at
 * join — and is rendered by the caller, because every surface already draws
 * names its own way. This supplies the two lines under it.
 *
 * `compact` is for the 110px polaroid and the 84px rail card, where the model
 * slug and the marker cannot both fit on one line without ellipsizing the model
 * into nothing.
 */
export const agentCaption = (u, { compact = false } = {}) => {
  if (!isExternalAgent(u)) return '';
  const model = agentModelName(u);
  const marker = isVisitingAgent(u) ? '🤖 visiting agent' : '🤖 agent';
  if (!model) return marker;
  if (compact) return model;
  return `${model}${agentModelIsReported(u) ? ' (reported by the agent)' : ''} · ${marker}`;
};

/**
 * 🪪 True when the model on this seat is the agent's own word (Nina 09-26:
 * "what it reports, marked"). A CONNECTED agent writes its own messages, so we
 * never know its model, only what it says; an imported seat's model is the one
 * the platform runs. The full caption (the hover text) says so, and the cast
 * card says it in words, "says it's …" (Nina 09-28: phones cannot hover).
 */
export const agentModelIsReported = (u) =>
  isExternalAgent(u) && isConnectedSeat(u) && !!agentModelName(u);
